/** Bridges CallKit/ConnectionService callUUID → chat params (works across background → JS). */
import EncryptedStorage from 'react-native-encrypted-storage';
import { Platform } from 'react-native';
import { voiceCallLog, voiceCallWarn } from '../calls/voiceCallDebug';
import { noteIncomingCallRinging } from '../calls/ringingIncomingCallRegistry';

type Pending = {
  chatId: string;
  callerName: string;
  callMode: 'audio' | 'video';
  callScope?: 'direct' | 'group';
  inviteId?: string;
  callerUserId?: string;
  callerHasAvatar?: boolean;
  callerAvatarRevision?: number;
  callerAvatarShape?: string | null;
  groupTitle?: string;
};

type PendingEntry = {
  payload: Pending;
  expiresAt: number;
};

/**
 * Хранилище pending-звонков. Память — быстрый путь для случая «JS жив»; EncryptedStorage —
 * долговременная копия, чтобы холодный старт активити (после ответа на CallKeep с заблокированного
 * экрана) мог восстановить chatId/callerName, когда headless-таск и UI-инстанс RN — разные
 * JS-контексты.
 */
const pendingByUuid = new Map<string, PendingEntry>();
const recentlyDisplayedCallKeys = new Map<string, number>();
const RECENT_CALL_TTL_MS = 20_000;
const PENDING_CALL_TTL_MS = 5 * 60_000;
const MAX_PENDING_CALLS = 128;

const STORAGE_KEY = 'vr_pending_calls_v1';
let storageHydrated = false;

function isWeb(): boolean {
  return Platform.OS === 'web';
}

function cleanupExpiredCallKeys(now: number): void {
  for (const [key, expiresAt] of recentlyDisplayedCallKeys.entries()) {
    if (expiresAt <= now) {
      recentlyDisplayedCallKeys.delete(key);
    }
  }
}

function normalizeCallKey(raw: string): string {
  return raw.trim().toLowerCase();
}

function cleanupExpiredPendingCalls(now: number): void {
  for (const [uuid, entry] of pendingByUuid.entries()) {
    if (entry.expiresAt <= now) {
      pendingByUuid.delete(uuid);
    }
  }
}

function trimPendingCallsToLimit(): void {
  while (pendingByUuid.size > MAX_PENDING_CALLS) {
    const first = pendingByUuid.keys().next().value as string | undefined;
    if (first == null) {
      break;
    }
    pendingByUuid.delete(first);
  }
}

function snapshotForStorage(): Record<string, PendingEntry> {
  const out: Record<string, PendingEntry> = {};
  for (const [uuid, entry] of pendingByUuid.entries()) {
    out[uuid] = entry;
  }
  return out;
}

async function persistPendingCalls(): Promise<void> {
  if (isWeb()) return;
  try {
    const raw = JSON.stringify(snapshotForStorage());
    await EncryptedStorage.setItem(STORAGE_KEY, raw);
  } catch (e) {
    voiceCallWarn('incomingCallBridge: persist failed', e);
  }
}

function isPendingEntry(value: unknown): value is PendingEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.expiresAt !== 'number') return false;
  if (!v.payload || typeof v.payload !== 'object') return false;
  const p = v.payload as Record<string, unknown>;
  return typeof p.chatId === 'string' && typeof p.callerName === 'string';
}

/**
 * Подгрузить состояние из EncryptedStorage (один раз на JS-контекст). Безопасно вызывать
 * многократно: ранее загруженные записи не теряются, повторный io не происходит.
 */
const HYDRATE_PENDING_CALLS_MS = 5_000;

export async function hydratePendingCalls(): Promise<void> {
  if (isWeb()) {
    storageHydrated = true;
    return;
  }
  if (storageHydrated) {
    return;
  }
  try {
    const raw = await Promise.race([
      EncryptedStorage.getItem(STORAGE_KEY),
      new Promise<string | null>((resolve) => {
        setTimeout(() => resolve(null), HYDRATE_PENDING_CALLS_MS);
      }),
    ]);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return;
    const now = Date.now();
    let added = 0;
    for (const [uuid, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!isPendingEntry(value)) continue;
      if (value.expiresAt <= now) continue;
      if (pendingByUuid.has(uuid)) continue;
      pendingByUuid.set(uuid, value);
      added += 1;
    }
    trimPendingCallsToLimit();
    if (added > 0) {
      voiceCallLog('incomingCallBridge: hydrated pending calls from storage', { added });
    }
  } catch (e) {
    voiceCallWarn('incomingCallBridge: hydrate failed', e);
  } finally {
    storageHydrated = true;
  }
}

export function storePendingCall(callUuid: string, payload: Pending): void {
  const now = Date.now();
  cleanupExpiredPendingCalls(now);
  pendingByUuid.set(callUuid, { payload, expiresAt: now + PENDING_CALL_TTL_MS });
  trimPendingCallsToLimit();
  void persistPendingCalls();
  /**
   * Здесь же — отметка «в этом чате звонит входящий».
   *
   * <p>Это единственная точка, через которую проходят все нативные пути входящего: VoIP-пуш
   * на iOS, фоновый FCM на Android, WS-кадр при живом JS. На iOS оверлея нет вовсе, и без
   * этой отметки отменять звонок, принятый на другом устройстве, было бы нечем — экран
   * рисует CallKit, а его UUID знает только это хранилище.
   */
  noteIncomingCallRinging({
    chatId: payload.chatId,
    inviteId: payload.inviteId ?? null,
    scope: payload.callScope ?? 'direct',
    callerUserId: payload.callerUserId ?? null,
    callUuid,
  });
}

/**
 * Забрать все UUID системного экрана, заведённые под звонок в этом чате.
 *
 * <p>Их обычно больше одного: тот же звонок доезжает и VoIP-пушем, и WS-кадром, а UUID у
 * них разные. Закрыть надо каждый — иначе на телефоне остаётся висеть фантомный «идёт
 * звонок», который уже никто не завершит.
 */
export function takePendingCallUuidsForChat(chatId: string): string[] {
  const now = Date.now();
  cleanupExpiredPendingCalls(now);
  const wanted = chatId.trim().toLowerCase();
  if (!wanted) {
    return [];
  }
  const uuids: string[] = [];
  for (const [uuid, entry] of pendingByUuid.entries()) {
    if (entry.payload.chatId.trim().toLowerCase() === wanted) {
      uuids.push(uuid);
      pendingByUuid.delete(uuid);
    }
  }
  if (uuids.length > 0) {
    void persistPendingCalls();
  }
  return uuids;
}

export function takePendingCall(callUuid: string): Pending | undefined {
  const now = Date.now();
  cleanupExpiredPendingCalls(now);
  const p = pendingByUuid.get(callUuid);
  pendingByUuid.delete(callUuid);
  void persistPendingCalls();
  if (p == null || p.expiresAt <= now) {
    return undefined;
  }
  return p.payload;
}

/**
 * Холодный старт после ответа из системного UI: callUUID известен, но в памяти его уже нет.
 * Если в storage только один pending — допустимо взять его как fallback.
 */
export function takeAnyPendingCall(): { uuid: string; payload: Pending } | undefined {
  const now = Date.now();
  cleanupExpiredPendingCalls(now);
  const it = pendingByUuid.entries().next();
  if (it.done) return undefined;
  const [uuid, entry] = it.value;
  pendingByUuid.delete(uuid);
  void persistPendingCalls();
  if (entry.expiresAt <= now) return undefined;
  return { uuid, payload: entry.payload };
}

/* ------------------------------------------------------------------ */
/* CallKit endCall fan-out                                              */
/* ------------------------------------------------------------------ */
/**
 * When the user presses the End button on the system CallKit/Telecom UI
 * (especially from the lock screen), `RNCallKeep.endCall` fires once at the
 * native bridge level. The currently active CallScreen / useVoiceCallSession
 * has no built-in way to hear that event, which left WebRTC streams alive,
 * the audio session locked, and the remote peer not receiving a `bye`.
 *
 * We expose a tiny pub/sub here so that AppNativeCallListeners can broadcast
 * to all interested call sessions: each session compares the incoming
 * callUUID against the one it owns and, on a match, runs its hangup path
 * (sendBye → teardownMedia → releaseCallKit → navigation.goBack).
 */
type CallKitEndListener = (callUuid: string) => void;
const callKitEndListeners = new Set<CallKitEndListener>();

export function subscribeCallKitEnd(fn: CallKitEndListener): () => void {
  callKitEndListeners.add(fn);
  return () => {
    callKitEndListeners.delete(fn);
  };
}

export function emitCallKitEnd(callUuid: string): void {
  for (const fn of callKitEndListeners) {
    try {
      fn(callUuid);
    } catch (e) {
      voiceCallWarn('callKitEnd listener threw', e);
    }
  }
}

export function shouldDisplayIncomingCall(callKey: string): boolean {
  const key = normalizeCallKey(callKey);
  if (!key) {
    voiceCallLog('incomingCall dedupe: empty key, allow display');
    return true;
  }
  const now = Date.now();
  cleanupExpiredCallKeys(now);
  const expiresAt = recentlyDisplayedCallKeys.get(key);
  if (expiresAt != null && expiresAt > now) {
    voiceCallLog('incomingCall dedupe: skip duplicate display', { key, expiresAt });
    return false;
  }
  recentlyDisplayedCallKeys.set(key, now + RECENT_CALL_TTL_MS);
  voiceCallLog('incomingCall dedupe: allow display', {
    key,
    ttlMs: RECENT_CALL_TTL_MS,
  });
  return true;
}
